/**
 * Color tokens — source of truth for the design-system layer.
 *
 * Four exports:
 *  - `brand`    : the Brand core V13 palette (the book's named colors, chapter 13).
 *  - `night`    : le versant sombre de cette palette — ce que le livre chiffre pour la nuit, et
 *                 ce qu'il faut en dériver pour les jetons qu'il ne montre pas (voir son en-tête).
 *  - `semantic` : DS-facing semantic tokens consumed by DS components and Unistyles themes.
 *  - `legacy`   : the flat palette of the legacy `src/common/theme/theme.js`. It no longer feeds
 *                 `semantic` (V13 replaced it) — it is kept as the documented legacy reference.
 *
 * Keys MUST NOT start with `$` — Unistyles v3 maps theme strings to CSS custom properties
 * on web (`--colors-primary`, etc.). `$` is invalid in CSS identifiers and breaks color
 * resolution in Storybook / react-native-web.
 *
 * The DS does NOT touch `theme.js`; legacy screens keep reading their own theme.
 */

// ───────────────────────────────────────────────────────────────────────────
// Brand core V13 — the book's palette. Every semantic token resolves to one of
// these, or to a tint computed from one (the tint's source is noted inline).
// ───────────────────────────────────────────────────────────────────────────
export const brand = {
    /** Socle commun — "marine base commune". */
    marine: "#1A2B66",
    /** Relief du bouton-poussoir parent : le marine assombri posé sous l'aplat (chap. 6). */
    marineShade: "#0C1640",
    /** Texte courant sur fond clair. */
    ink: "#0F1B3D",
    /** Nuit — le fond sombre. */
    night: "#0B1533",
    /** Surface sombre posée sur la nuit. */
    surfaceNight: "#152452",
    /**
     * Le fond d'une tuile vidéo, presque noir (Wajih, 05/11 : « un fond plutôt noir que bleu ») : hors du livre, qui
     * ne chiffre pas la vidéo. Une pointe de la nuit pour ne pas trouer la page sombre.
     */
    videoBlack: "#0E1017",
    /** Jaune signature. Toujours accroché au marine, jamais seul sur la feuille vide. */
    yellow: "#FFE000",
    /** Ombre du jaune signature (nœud papillon, pli). */
    yellowShade: "#E0C500",

    /** Univers parent. */
    roleParent: "#1A2B66",
    /** Univers élève. */
    roleStudent: "#EE5F49",
    /** Univers tuteur. */
    roleTutor: "#1F7A8C",
    /**
     * Le BOUTON de l'univers élève : un ORANGE (décision de Wajih, 15/10 : le corail du kit V16,
     * `#D23F28`, « un peu trop rouge »). Le corail d'univers `#EE5F49` reste celui des chips, des
     * rubans et des icônes — le blanc n'y tient que 3,3:1. Ici il tient 4,63:1 : au-delà, un orange
     * plus clair ne porte plus le blanc. Teinte 20°, loin de l'orange d'alerte `#E8890C` (34°,
     * ΔE 20). À écrire dans le livre V17 (`univers.eleve.principal`).
     */
    roleStudentFill: "#C55016",
    /**
     * Reliefs des boutons élève et tuteur (kit V16 : `univers.*.relief`). Celui de l'élève assombrit
     * son orange du pas du kit (`#D23F28` → `#A32D1A` : la luminance × 0,56). C'est aussi l'encre de
     * sa chip de gain, comme le ton foncé de la progression l'est sur sa tinte.
     */
    roleStudentShade: "#993B0D",
    roleTutorShade: "#155866",
    /**
     * L'univers élève quand il s'ÉCRIT sur du clair (kit V16 : `univers.eleve.secondaire-texte`) :
     * le libellé du secondaire et du tertiaire, l'option non choisie, l'onglet ouvert. L'orange du
     * bouton n'y tient que 4,3:1 sur la page ; celui-ci, le même orange un cran plus foncé, tient 5,0
     * sur une carte et 4,6 sur la page — les chiffres du corail d'écriture du kit qu'il remplace.
     */
    roleStudentInk: "#BC4C15",
    /**
     * L'univers élève quand il s'AFFICHE dans son propre espace (anneaux, segments, puces, glyphes du
     * jeu) : l'orange du bouton porté à la clarté du corail d'univers, pour qu'un écran de l'élève
     * n'ait qu'un orange (le corail `#EE5F49` y faisait une seconde famille, ΔE 13 avec le bouton).
     * Mêmes contrastes que le corail : 3,1 sur la page, 3,3 sur une carte, 5,4 sur la nuit. Lu par
     * `role` des deux thèmes élève seulement : le corail reste celui des autres univers.
     */
    roleStudentDisplay: "#EA6421",

    /** Progression — le seul vert. */
    progress: "#0F9E70",
    /** Texte de progression posé sur sa tinte. */
    progressInk: "#0A6B4C",
    /** Alerte. */
    alert: "#E8890C",
    /** Erreur. */
    error: "#D9534F",
    /**
     * Encres d'alerte et d'erreur posées sur leur tinte. Le livre ne chiffre que celle de la
     * progression (`#0F9E70` → `#0A6B4C`) ; les deux autres suivent le même assombrissement,
     * parce que l'orange et le rouge pleins sur leur tinte tombent à 2,3 et 3,3 de contraste —
     * sous l'AA que le livre exige « partout ».
     */
    alertInk: "#905507",
    errorInk: "#873331",
    /**
     * Le relief du bouton qui détruit, chez l'élève : son aplat y est l'encre d'erreur (le rouge
     * plein se confondait avec son bouton), et ce relief l'assombrit du même pas (× 0,62)
     * que l'encre assombrit le rouge plein. Proposé : le livre ne le chiffre pas.
     */
    errorInkShade: "#54201E",
    /** Concentré — surface LIVE uniquement. */
    focused: "#7C3AED",
    /**
     * Le relief du poussoir Concentré (la bascule Dialogue · Concentré du live du masteur) : le violet assombri du même
     * pas (× 0,62) que `errorInkShade` assombrit l'encre d'erreur. Proposé : le livre ne le chiffre pas.
     */
    focusedShade: "#4D2493",

    /** Fonds. */
    page: "#F5F6F8",
    white: "#FFFFFF",
    lavender: "#F3F5FF",
    black: "#000000",

    // Tintes claires — `rgba(<couleur>, .14)` aplatie sur blanc, pour les bandeaux et
    // les chips teintés (le calque alpha ne survit pas à un fond non blanc en RN).
    progressTint: "#DDF1EB",
    alertTint: "#FCEEDD",
    errorTint: "#FAE7E6",
    /**
     * La tinte du corail d'univers, le fond de la chip de gain (« +25 XP », chap. 03 et 06 ; mesurée
     * `#FDE8E5` p. 14). L'encre d'écriture de l'élève n'y tient que 4,3:1 : la chip s'écrit dans
     * son ton foncé (`roleStudentShade`, 6,0:1).
     */
    roleStudentTint: "#FDE9E6",
    /** La tinte de cet orange (`rgba(#C55016, .14)` sur blanc) : le fond de la chip de gain chez l'élève. */
    roleStudentDisplayTint: "#F7E7DE",

    // Gris marine — `rgba(11,21,51,.65)` / `rgba(11,21,51,.10)` aplatis sur blanc : le texte
    // secondaire et les filets du livre ne sont jamais des gris neutres, ils sont teintés marine.
    // Le texte secondaire est passé de 62 % à 65 % avec le livre V16 (`#686E80` → `#60677A`).
    inkMuted: "#60677A",
    /**
     * Le fond de l'initiale quand un visage n'a pas de photo — la capsule du tuteur du kit V16
     * (« sans photo : initiale sur #E3E8F6 »), un bleu marine très clair, plus présent que la
     * lavande des encarts.
     */
    initialsFill: "#E3E8F6",
    /**
     * Le contour du livre : celui du bouton secondaire, et le même sur un champ, une carte
     * d'option, une grille. Le livre n'en chiffre qu'un — un contour de plus serait un contour
     * qui dérive.
     */
    hairline: "#D5D9E6",
    hairlineSubtle: "#E7E8EB",
    /**
     * Le filet qui SÉPARE deux lignes d'une même carte : `rgba(15,27,61,.08)` aplati sur blanc.
     * Une carte n'a plus de contour en V13 (« cartes blanches sans filet ») ; ce jeton ne sert
     * plus qu'au découpage interne d'une `<CardList>`.
     */
    hairlineCard: "#EBECF0",
    /** Voile des overlays — nuit à 55 %. */
    scrim: "#0B15338C",
    /**
     * Le voile de l'élève — nuit à 70 % (Wajih, 20/10 : ses feuilles « ne se détachent pas assez de
     * l'arrière-plan »). La feuille blanche passe de 4,3:1 à 7,1:1 contre le voile posé sur la page.
     */
    scrimDeep: "#0B1533B3",

    // Champions V13 (chap. 11) — seules leurs couleurs PLEINES servent : ce sont les quatre
    // nœuds du dégradé des temps forts. Le nœud papillon de la capsule, lui, est jaune pour tous
    // depuis la V13, et son ombre est `accent-strong` : les quatre tons foncés de champion
    // n'avaient plus de lecteur.
    championZoe: "#FC9F51",
    championAbou: "#D2B6F5",
    championSam: "#8ABFEA",
    championTina: "#55CC8F",

    /**
     * L'aurore des champions (V16, chap. 03 et 06) : les cinq couleurs du cadre des temps forts,
     * chacune à son opacité — `rgba(255,224,0,.22)`, `rgba(252,159,81,.16)`, `rgba(85,204,143,.16)`,
     * `rgba(138,191,234,.22)`, `rgba(210,182,245,.24)`. L'opacité est écrite dans la couleur
     * (`#RRGGBBAA`, arrondie au 1/255) : le dégradé se pose sur la surface d'une carte, blanche ou
     * nuit, et le même calque vaut pour les deux — c'est ce que le livre écrit, « sur #FFFFFF (ou
     * #152452 en sombre) ».
     */
    auroraYellow: "#FFE00038",
    auroraZoe: "#FC9F5129",
    auroraTina: "#55CC8F29",
    auroraSam: "#8ABFEA38",
    auroraAbou: "#D2B6F53D",
} as const

// ───────────────────────────────────────────────────────────────────────────
// Le versant nuit de la palette (Brand core V13, « Clair et sombre, au choix »).
//
// Le livre ne chiffre que DEUX valeurs sombres — Nuit `#0B1533` et Surface nuit `#152452`
// (chap. 13) — plus, en image, le bouton parent de la page « MODE SOMBRE » (chap. 06) et les
// chips posées sur la nuit (chap. 03). Tout le reste se DÉRIVE, et se dérive comme le clair :
//
//   clair : `rgba(<encre>, α)` aplatie sur **blanc**      → #60677A, #E7E8EB, #EBECF0, les tintes
//   nuit  : `rgba(<couleur>, α)` aplatie sur **la nuit**  → tout ce qui suit
//
// Aplatir plutôt que superposer, parce qu'un calque alpha ne survit pas à un fond non blanc en
// React Native — c'est déjà la raison des tintes claires. Les α sont ceux du clair, repris tels
// quels quand ils ont un équivalent (0,65 pour le texte secondaire), et mesurés sur le livre
// quand il les montre (0,25 pour la tinte d'une chip, chap. 03).
// ───────────────────────────────────────────────────────────────────────────
export const night = {
    /** Le fond de page, uni : « en sombre, le fond reste uni » (chap. 03). */
    page: brand.night,
    /** La carte posée sur la nuit — `Surface nuit` du chap. 13. */
    surface: brand.surfaceNight,

    /**
     * La surface surélevée : `rgba(#FFFFFF, .09)` aplatie sur `#152452`. C'est l'aplat des
     * encarts, là où le clair pose sa lavande — un bloc teinté de l'univers, et sur la nuit
     * l'univers du parent, c'est le blanc.
     */
    raised: "#2A3862",

    /**
     * Texte secondaire : `rgba(#FFFFFF, .65)` aplatie sur la nuit — le même 65 % que l'encre
     * sourde du clair (V16), pris de l'autre côté.
     */
    muted: "#AAADB8",

    /**
     * Les trois filets, `rgba(#FFFFFF, α)` aplaties sur `#152452`, dans l'ordre de visibilité du
     * clair (`border` > `border-subtle` > `border-card`). Le contour du livre monte à 35 % :
     * c'est l'alpha à partir duquel un contour de 2 px tient les 3:1 d'une limite non textuelle
     * sur une carte sombre — 20 % (l'alpha apparent du `#D5D9E6` sur blanc) n'y arrive pas.
     */
    border: "#67718F",
    borderSubtle: "#445075",
    borderCard: "#313E67",

    /**
     * Relief des boutons blancs de la page « MODE SOMBRE » (chap. 06) — le principal parent, dont
     * la face passe au blanc et le libellé au marine, et le secondaire, qui garde sa face blanche.
     * Le livre V16 le chiffre enfin (« relief #C9CEDA ») ; la V13 ne le montrait qu'en image, et
     * on l'y avait mesuré à `#C9CDD9`.
     */
    relief: "#C9CEDA",

    /**
     * Les tintes de chip : `rgba(<couleur pleine>, .25)` aplatie sur la nuit. 25 % est ce que
     * mesurent les chips du chap. 03 sur fond nuit (« +8 % » parent, « +25 XP » élève).
     */
    progressTint: "#0C3742",
    alertTint: "#423229",
    errorTint: "#3E243A",

    /**
     * Les encres posées sur ces tintes : la couleur pleine éclaircie de 45 % vers le blanc. Le
     * clair assombrit pour tenir l'AA, la nuit éclaircit — même geste, sens inverse (6,6 à 7,2
     * de contraste sur leur tinte).
     */
    progressInk: "#7BCAB0",
    alertInk: "#F2BE79",
    errorInk: "#EAA09E",

    /**
     * L'univers élève quand il s'ÉCRIT sur la nuit : l'orange de son bouton, éclairci jusqu'aux
     * chiffres de l'encre corail qu'il remplace (9,4 sur la nuit, 7,8 sur une carte, 6,8 sur la
     * tinte de la chip de gain), à la même saturation. Le livre le montre sans le chiffrer —
     * « 1 240 XP », « Apprenti », l'encre des chips de gain (chap. 16), mesurés autour de `#F4B3A3`.
     */
    roleStudentInk: "#F4AA86",

    /**
     * Trois teintes que le livre ne montre qu'en IMAGE pour l'élève, dérivées comme le reste et
     * **à valider par la marque**.
     *  - la chip de gain « +25 XP » : `rgba(corail, .25)` sur la nuit, la tinte des autres chips
     *    (mesurée `#462839`, chap. 03). Lue par `role-student-tint`, avec l'encre
     *    `roleStudentInk` (6,8:1) ;
     *  - la piste de l'anneau de niveau et les segments éteints : le même calque sur la SURFACE
     *    nuit, puisqu'ils vivent dans une carte (mesurés `#50344F` et `#45304F`, chap. 16). Sans
     *    lecteur ;
     *  - le fond de la carte « Prochain LIVE » de l'accueil projeté : `rgba(corail, .14)` sur la
     *    nuit, l'α des tintes claires (mesuré `#2D1F36`, chap. 17). Sans lecteur.
     */
    roleStudentTint: "#442839",
    /**
     * La tinte de gain de l'élève sur la nuit, à son orange : `rgba(#C55016, .25)` aplatie sur la
     * CARTE, pas sur la page — voir les tintes posées sur une carte, juste dessous.
     */
    roleStudentDisplayTint: "#412F43",

    /**
     * Les tintes de statut de l'ÉLÈVE sur la nuit : le même calque à 25 %, aplati sur la CARTE
     * (`#152452`) et non sur la page. Une tinte se pose presque toujours dans une carte — la chip
     * d'un point de correction, le rond d'une icône dans une ligne, la chip de gain — et, aplatie
     * sur la page, elle s'y fondait : 1,04 à 1,17 contre la carte, sous la marche carte / page du
     * livre (1,20). Aplatie sur la carte, elle tient cette marche des deux côtés : 1,22 à 1,45
     * contre la carte, 1,46 à 1,74 contre la page ; son encre y tient 5,5 à 6,4. Lues par
     * `studentDark` seulement : la nuit du parent garde les tintes posées sur la page (proposé).
     */
    progressTintOnCard: "#14435A",
    alertTintOnCard: "#4A3D41",
    errorTintOnCard: "#463051",

    /**
     * Le teal du tuteur et le violet du Concentré quand ils s'ÉCRIVENT sur la nuit (les axes
     * « Participation » et « Productivité » du score d'engagement) : la couleur pleine éclaircie de
     * 45 % vers le blanc, comme les encres de progression, d'alerte et d'erreur. Pleins, ils ne
     * tenaient que 3,0 et 2,6 sur une carte de nuit ; ainsi 6,7 et 6,1.
     */
    tutorInk: "#84B6C0",
    focusedInk: "#B793F5",
    roleStudentTrack: "#4B3350",
    roleStudentWash: "#2B1F36",

    /**
     * Voile des overlays sur la nuit. Le voile clair (nuit à 55 %) ne sépare plus rien quand la
     * page EST la nuit : c'est le noir à 65 % qui refait le même geste — pousser la page au fond.
     */
    scrim: "#000000A6",
    /**
     * Le voile de l'élève sur la nuit — le noir à 80 %. La page étant déjà presque noire, le voile ne
     * gagne presque rien ici (la feuille passe de 1,35 à 1,37:1, plafond 1,39 contre le noir pur) :
     * sur la nuit, c'est la clarté de la feuille qui la détache (`contrast.test.ts`).
     */
    scrimDeep: "#000000CC",

    /**
     * Les couleurs de matière, éclaircies de 55 % vers le blanc. Elles viennent du seeder
     * backend, hors palette V13, et plusieurs sont trop foncées pour la nuit telles quelles
     * (`#000080` en physique, `#8B0000` en histoire). Le même éclaircissement pour toutes, pour
     * qu'aucune ne dérive de son hue.
     */
    subjectMaths: "#8CDAF6",
    subjectPhysics: "#8C8CC6",
    subjectBiology: "#FFCC8C",
    subjectFrench: "#FF8C8C",
    subjectHistory: "#CB8C8C",
    subjectEnglish: "#8CC68C",
    subjectSpanish: "#FFED8C",
    subjectGerman: "#CAB6A3",
    subjectPortuguese: "#E890AA",
    subjectPhilosophy: "#C68CC6",
    subjectEconomics: "#FFD68C",
    subjectEmc: "#FF8CFF",
    subjectItalian: "#E89B9F",
    subjectDefault: "#98A0BA",
} as const

// ───────────────────────────────────────────────────────────────────────────
// Legacy reference — the flat palette of `src/common/theme/theme.js`.
// Kept for traceability while legacy screens are migrated; NOT a DS surface.
// ───────────────────────────────────────────────────────────────────────────
export const legacy = {
    background: "#fafafa",
    primaryDark: "#101d40",
    primary: "#223F8F",
    primaryTransparent: "#223F8FAA",
    primaryLight: "#5a6da1",
    primaryLighter: "#f3f5ff",
    accentDark: "#f1ba00",
    accent: "#ffc80a",
    accentLight: "#FFDC64",
    accentLighter: "#fff1db",
    successDark: "#249e7f",
    success: "#00BE89",
    successTransparent: "#01AE5177",
    phone: "#4CABB4",
    phoneLight: "#cdfbff",
    successLighter: "#59bba0",
    successGray: "#bad3cb",
    successLight: "#f1fff3",
    successLightMore: "#f7fff8",
    warning: "#ff9f43",
    warningLight: "#fff1e6",
    warningLighter: "#ffb980",
    error: "#ea5455",
    errorLight: "#ffe5e5",
    errorLighter: "#ff7171",
    errorDark: "#662526",
    brown: "#895D33",
    brownLight: "#f5ebe0",
    info: "#00cfe8",
    infoLight: "#e5f9fc",
    white: "#ffffff",
    whiteTransparentMore: "#ffffffDD",
    whiteTransparent: "#ffffff44",
    light: "#aaaaaa",
    lightMore: "#e2e2e2",
    lightMoreTransparent: "#e2e2e244",
    lighter: "#F3F3F3",
    whiteAlmost: "#fafafa",
    linkAlt: "#662FFF",
    link: "#2F66FF",
    linkLight: "#779bff",
    linkLighter: "#e0ebff",
    xp: "#01AE51",
    xpText: "#004D24",
    feather: "#2D2F38",
    chronoBlue: "#3A86FF",
    greyAlt: "#6E6E6E",
    grey: "#505050",
    greyBg: "#F2F2F2",
    black: "#000000",
    blackAlmost: "#141414",
    blackLight: "#2D2F30",
    blackTransparent: "#00000033",
    blackTransparent2: "#00000066",
    petal: "#9DD767",
    petalDarker: "#80af54",
    petalNew: "#D3918F",
    petalNewDarker: "#985857",
    petalNewLighter: "#ffe6e6",
    gems: "#BC3B60",
    gemsLight: "#ffe5ed",
    transparent: "#FFFFFF00",
    purple: "#7A6EB5",
    purpleTransparent: "#7A6EB588",
    purpleLight: "#faf9ff",
    mutedOrange: "#FF7F50",
    zoe: "#D98759",
    zoeLight: "#F2EB64",
    abou: "#9650C9",
    abouLight: "#DCC1F7",
    sam: "#3B708E",
    samLight: "#8ABFEA",
    tina: "#45D090",
    tinaLight: "#9CF6C7",
} as const

// ───────────────────────────────────────────────────────────────────────────
// Semantic tokens — the DS-facing surface.
// Every DS component built in src/design-system/atoms/* (and up) MUST use these,
// NOT the flat `brand` / `legacy` keys above.
// ───────────────────────────────────────────────────────────────────────────
export const semantic = {
    // brand
    primary: brand.marine,
    "primary-strong": brand.night,
    "primary-soft": brand.lavender,
    /**
     * Le marine en grande SURFACE — un bandeau de marque, pas un bouton. Il ne s'inverse dans
     * aucun des deux thèmes, contrairement à `primary` : l'inversion de l'univers est juste sur
     * un poussoir de 44 px (le livre la dessine ainsi), elle perce un trou de lumière dès qu'elle
     * peint 100 px de haut sur la nuit. Son encre est `white`, celle des aplats foncés qui ne
     * bougent pas.
     */
    "brand-fill": brand.marine,
    /** Jaune signature. Ne peint jamais un bouton ni un titre — accent + anneau de focus. */
    accent: brand.yellow,
    "accent-strong": brand.yellowShade,

    /**
     * Relief des boutons-poussoirs (chap. 6) — l'aplat franc posé SOUS le bouton, jamais une
     * ombre floue. Le livre n'en chiffre que deux : le principal de l'espace parent et le
     * secondaire. Les autres couleurs reprennent leur encre foncée (`danger-strong`, …) ; la
     * table est dans `<Button>`.
     */
    "relief-primary": brand.marineShade,
    "relief-secondary": brand.hairline,
    /**
     * Le relief d'un bouton posé sur l'aplat de marque (`brand-fill`) : le même ton foncé que le
     * principal, mais qui ne s'inverse PAS sur la nuit — `relief-primary` y passe au gris des
     * boutons blancs, et un bouton marine garderait un relief gris.
     */
    "relief-brand": brand.marineShade,
    /**
     * Le contour 2 px du bouton secondaire. En clair, c'est LE contour du livre (`border`) ; il
     * s'en sépare sur la nuit, où le secondaire garde sa face blanche et son contour `#D5D9E6`
     * (chap. 06, « en sombre il garde son fond blanc ») pendant que `border` s'assombrit pour les
     * champs et les grilles posés sur la nuit.
     */
    "border-secondary": brand.hairline,
    /**
     * Le relief des boutons de couleur. Il valait `*-strong` — la même valeur que l'encre posée
     * sur la tinte — et les deux se séparent en mode sombre : sur la nuit l'encre s'éclaircit
     * (elle se pose sur un fond foncé) alors que le relief doit rester foncé (il se pose sous un
     * aplat de couleur). Un relief plus clair que sa face éclairerait le bouton par en dessous.
     * Les valeurs claires sont inchangées : ce sont celles que `*-strong` portait.
     */
    "relief-success": brand.progressInk,
    "relief-warning": brand.alertInk,
    "relief-danger": brand.errorInk,
    "relief-focused": brand.focusedShade,

    /**
     * Couleur de l'univers courant. Le thème de chaque persona la repointe :
     * parent = marine, élève = corail, tuteur = teal. « Jamais mélangées. »
     */
    role: brand.roleParent,
    "role-parent": brand.roleParent,
    "role-student": brand.roleStudent,
    "role-tutor": brand.roleTutor,
    /**
     * Le bouton élève : `role-student` est la couleur d'AFFICHAGE de l'univers (chips, rubans),
     * celui-ci l'aplat de son bouton-poussoir. Le tuteur n'en a pas besoin — son bouton est
     * `role-tutor` lui-même.
     */
    "role-student-fill": brand.roleStudentFill,
    "relief-student": brand.roleStudentShade,
    "relief-tutor": brand.roleTutorShade,
    /**
     * La chip de gain de l'élève en TINTE (« +25 XP ») : le fond et l'encre de la version claire de
     * `role-student-fill`. La nuit les repointe (la tinte s'aplatit sur la nuit, l'encre s'éclaircit),
     * comme les tintes de statut.
     */
    "role-student-tint": brand.roleStudentTint,
    "role-student-tint-ink": brand.roleStudentShade,

    /**
     * Le BOUTON de l'univers courant — ce que le kit appelle `univers.<x>.principal`, `.relief` et
     * `.texte`. `<Button>` les lit par défaut, et chaque thème les repointe : marine sur son ton
     * foncé pour le parent (blanc à libellé marine sur la nuit), orange de bouton pour l'élève,
     * dans les deux versants. Ils valent ici ce que valent `primary`, `relief-primary` et
     * `text-on-primary` : un thème qui ne les repointe pas garde son bouton marine.
     *
     * À distinguer de `primary`, qui reste le marine du SOCLE (titres, icônes) dans tous les
     * univers, et de `role`, la couleur d'AFFICHAGE de l'univers — chez l'élève, le blanc ne
     * tient que 3,3:1 sur `role`, d'où un aplat de bouton plus sombre.
     */
    "role-fill": brand.marine,
    "relief-role": brand.marineShade,
    "text-on-role": brand.white,
    /**
     * L'univers quand il s'ÉCRIT à même une surface, pour une ACTION : le libellé du bouton
     * tertiaire, un lien. Il suit le bouton (`role-fill`), pas la couleur d'affichage.
     */
    "text-link": brand.marine,
    /**
     * L'univers quand il s'écrit pour une SÉLECTION — l'option non choisie, l'onglet ouvert — et
     * sur sa tinte. C'est l'encre de `role`, comme `success-strong` est celle de `success` :
     * égale à `role` tant que celui-ci porte un texte à l'AA (marine, teal), assombrie chez
     * l'élève, éclaircie sur la nuit.
     */
    "role-strong": brand.roleParent,
    /**
     * L'univers quand il dit CHOISI : l'aplat d'une option choisie (`<ChoiceChip>`, pilule et chip,
     * la chip d'enfant choisie). Il vaut `role` dans chaque univers, sauf chez l'élève : le blanc ne
     * tient que 3,3:1 sur son corail d'univers, et l'option choisie y prend l'orange de son bouton
     * (4,6:1). `role` reste alors une couleur d'affichage, qui ne porte pas de texte.
     */
    "role-selected": brand.roleParent,

    // status
    success: brand.progress,
    successLight: brand.progressTint,
    /** Texte de progression posé sur `successLight`. */
    "success-strong": brand.progressInk,
    warning: brand.alert,
    warningLight: brand.alertTint,
    /** Texte d'alerte posé sur `warningLight`. */
    "warning-strong": brand.alertInk,
    danger: brand.error,
    dangerLight: brand.errorTint,
    /** Texte d'erreur posé sur `dangerLight`. */
    "danger-strong": brand.errorInk,

    /**
     * Les deux aplats FONCÉS d'alerte et d'erreur — la seule façon d'écrire un statut grave en
     * blanc sans tomber sous l'AA : l'orange plein n'y porte le blanc qu'à 2,6 et le rouge plein
     * qu'à 4,0 (l'écart connu du livre), alors que ceux-ci tiennent 6,0 et 8,2.
     *
     * Ils ne s'inversent PAS, comme `brand-fill` et pour la même raison : ce sont des aplats de
     * couleur, pas des tons de fond. À ne pas confondre avec les ENCRES `warning-strong` /
     * `danger-strong`, qui portent les mêmes valeurs en clair mais s'éclaircissent sur la nuit —
     * une encre se pose sur un fond, un aplat EST le fond.
     */
    "warning-deep": brand.alertInk,
    "danger-deep": brand.errorInk,
    /**
     * L'aplat d'un bouton qui DÉTRUIT (`<Button color="danger">` plein, la confirmation d'une boîte
     * destructrice, le bouton d'icône qui supprime). Il vaut `danger` partout, sauf chez l'élève :
     * ce qui détruit y prend l'aplat foncé, qui s'écarte de son bouton en teinte ET en clarté (ΔE 20,3,
     * 1,78:1 ; blanc à 8,2:1), là où le rouge plein n'en diffère qu'en teinte (ΔE 14,4, 1,17:1).
     * Décidé contre le corail du 13/10 (ΔE 8,3), re-mesuré contre l'orange du 15/10. Le rouge d'un
     * message, d'un bandeau, d'une chip ou d'un contour reste `danger`.
     */
    "danger-fill": brand.error,
    // Le livre ne définit pas de bleu d'information : l'info parle marine sur lavande.
    info: brand.marine,
    infoLight: brand.lavender,

    // extended accents (engagement sub-score axes)
    phone: brand.roleTutor,
    /** Concentré — réservé aux surfaces LIVE. */
    purple: brand.focused,

    // surfaces
    surface: brand.white,
    "surface-raised": brand.white,
    "surface-subtle": brand.page,
    /** Le fond de l'initiale d'un visage sans photo (`<Avatar>`). */
    "initials-fill": brand.initialsFill,
    /**
     * La couleur de la lueur d'accueil (V16, chap. 03) — le jaune signature, **opaque** : `<Glow>`
     * règle l'opacité le long de l'ellipse par `stopOpacity` (`tokens/effects` → `glow`), parce que
     * `react-native-svg` natif ignore l'alpha de la couleur d'un arrêt. Sur la nuit, la lueur
     * n'est pas montée : aucun thème sombre n'a à repointer ce jeton.
     */
    glow: brand.yellow,
    /**
     * Le trait qui balaie un squelette de chargement (`<Skeleton>` sous `<SkeletonGroup>`) —
     * **opaque**, comme `glow` : l'opacité est celle de la recette (`atoms/Skeleton/skeletonSweep`).
     * En clair, le jaune du surligneur, sous le bas de la barre. Sur la nuit, le thème le repointe
     * au blanc : un jaune léger sur le marine s'y lit gris, un jaune appuyé y tourne à l'olive.
     */
    "skeleton-sweep": brand.yellow,
    /** Surface sombre posée sur la nuit (mode sombre). */
    "surface-night": brand.surfaceNight,
    /** Le fond d'une tuile vidéo (`<VideoTile>`), le même dans tous les thèmes : une vidéo n'a pas de clair. */
    "surface-video": brand.videoBlack,
    /**
     * La surface de ce qui FLOTTE au-dessus du contenu — l'aperçu de ce que Sam vient de dire, le
     * rond « aller au dernier message » posé sur le fil. Elle peut couvrir la page comme une carte.
     * En clair, elle vaut la carte : c'est son ombre qui la fait flotter. Sur la nuit l'ombre ne se
     * voit pas et la profondeur se lit à la clarté : page, carte, puis ce qui flotte, une marche plus
     * clair que tout ce qu'il peut couvrir. Le thème élève sombre la monte d'une marche ; ailleurs
     * elle vaut encore `surface-raised` (la nuit du parent l'adoptera par une ligne, proposé).
     */
    "surface-floating": brand.white,
    /**
     * Le liseré du bord haut d'une feuille qui monte du bas (1 px, `SHEET_EDGE_WIDTH`). Il vaut la
     * surface de la feuille en clair — il ne se voit pas, l'ombre suffit — et prend le contour de la
     * nuit sur la nuit, élève et parent : la feuille ne peut plus s'y détacher du voile par sa clarté
     * (Wajih, 20/10).
     */
    "sheet-edge": brand.white,
    /**
     * Le contour d'un bouton dont l'état tient à son aplat clair, quand l'aplat ne se détache pas de
     * ce qui le porte : la main levée du live, jaune sur la barre blanche (1,2:1). En clair, l'encre ;
     * sur la nuit, transparent (le jaune s'y détache seul). Une couleur, pas une branche sur le thème.
     */
    "raised-ring": brand.ink,

    // text
    "text-primary": brand.ink,
    "text-strong": brand.night,
    "text-muted": brand.inkMuted,
    "text-on-primary": brand.white,
    /** Texte posé sur la nuit — blanc : le crème est sorti de la palette en V13. */
    "text-on-night": brand.white,
    /**
     * L'encre posée sur un aplat CLAIR — le jaune signature (« le fluo passe sur l'encre », la
     * chip « Préparée » ne s'écrit qu'en marine) et l'orange d'alerte. Marine dans les deux
     * thèmes, parce que l'aplat, lui, est clair dans les deux. C'est le pendant de `white`,
     * l'encre des aplats foncés : ni l'une ni l'autre ne suit l'inversion de l'univers.
     */
    "text-on-accent": brand.marine,
    /**
     * Le libellé du bouton SECONDAIRE : la couleur de l'univers, posée sur une face blanche dans
     * les deux thèmes (kit V16 : `univers.<x>.secondaire-texte`). Marine pour le parent, clair
     * comme sombre — sur la nuit, l'univers s'inverse et `primary` passe au blanc, mais la face du
     * secondaire, elle, reste blanche. Le thème élève le repointe sur son orange d'écriture.
     */
    "text-on-secondary": brand.marine,
    /**
     * Une couleur de statut ou d'axe quand elle S'ÉCRIT à même une surface : un message d'erreur, un
     * bandeau d'alerte, « Très bien » sous la jauge, l'axe « Participation ». Le descripteur les
     * nomme (`fills.<couleur>.text`), et un composant qui écrit une couleur la lit par
     * `textInkOnFill`. Ils valent ici l'aplat lui-même — ce que le parent écrit encore : le rouge
     * plein n'y tient que 4,0 sur le blanc, l'orange d'alerte 2,4, le vert 3,4. L'élève les repointe
     * sur leurs encres (`*-strong`, et le teal et le violet éclaircis sur sa nuit) ; le parent
     * l'adoptera par une ligne par jeton (proposé).
     */
    "text-success": brand.progress,
    "text-warning": brand.alert,
    "text-danger": brand.error,
    "text-phone": brand.roleTutor,
    "text-purple": brand.focused,

    // borders / separators
    /** Le contour du livre : bouton secondaire, champ, carte d'option, grille. */
    border: brand.hairline,
    "border-subtle": brand.hairlineSubtle,
    /** Le filet qui sépare deux lignes d'une même carte. Une carte n'a plus de contour. */
    "border-card": brand.hairlineCard,

    // overlay / scrim
    /** Le calque sombre posé sur une vidéo, sous un nom ou un bouton (vignette, scène, aperçu). */
    backdrop: brand.scrim,
    /**
     * Le voile qu'`<Overlay>` pose sous une feuille ou une boîte. Il vaut `backdrop` partout, sauf
     * chez l'élève, qui l'assombrit (`scrimDeep`) : une valeur à part, pour que ni le parent ni les
     * calques posés sur une vidéo ne bougent.
     */
    scrim: brand.scrim,
    black: brand.black,
    /**
     * Le blanc qui ne s'inverse PAS, à distinguer de `text-on-primary` : celui-ci suit l'aplat de
     * l'univers et devient marine sur la nuit (le bouton parent y est blanc), alors que l'aplat
     * d'une couleur pleine — vert de progression, rouge d'erreur, matière — reste le même dans
     * les deux thèmes, donc l'encre posée dessus aussi. Sert aussi au liseré de la capsule du
     * tuteur (« liseré blanc 3 px », chap. 11) et au voile de survol du bouton principal.
     */
    white: brand.white,
    /**
     * L'encre d'une ombre portée. Le livre ne connaît pas de gris neutre : l'ombre se peint dans
     * l'encre du texte. Sur la nuit elle passe au noir, où elle ne se voit plus — et c'est voulu,
     * une carte sombre se lit à sa surface (`#152452` sur `#0B1533`), pas à son ombre.
     */
    shadow: brand.ink,

    // school subjects — hex from SchoolSubjectSeeder; mapped in ports adapters (never pass API hex into DS)
    "subject-maths": "#00ADEA",
    "subject-physics": "#000080",
    "subject-biology": "#ff8d00",
    "subject-french": "#FF0000",
    "subject-history": "#8B0000",
    "subject-english": "#008000",
    "subject-spanish": "#FFD700",
    "subject-german": "#895D33",
    "subject-portuguese": "#CC0842",
    "subject-philosophy": "#800080",
    "subject-economics": "#FFA500",
    "subject-emc": "#FF00FF",
    "subject-italian": "#CD212A",
    "subject-default": brand.marine,

    // Champions — les quatre nœuds du dégradé des temps forts (`<HighlightFrame>`).
    "champion-zoe": brand.championZoe,
    "champion-abou": brand.championAbou,
    "champion-sam": brand.championSam,
    "champion-tina": brand.championTina,

    // L'aurore des champions (`<AuroraFill>`) — les mêmes cinq couleurs, en calque. Elle ne
    // s'inverse pas : c'est la surface de la carte dessous qui passe du blanc à la nuit.
    "aurora-accent": brand.auroraYellow,
    "aurora-zoe": brand.auroraZoe,
    "aurora-tina": brand.auroraTina,
    "aurora-sam": brand.auroraSam,
    "aurora-abou": brand.auroraAbou,
} as const

export type SemanticColorKey = keyof typeof semantic
export type BrandColorKey = keyof typeof brand
export type LegacyColorKey = keyof typeof legacy

// ───────────────────────────────────────────────────────────────────────────
// Les aplats — ce qui se pose SUR une couleur pleine.
//
// Un aplat n'est pas qu'une couleur de fond : il traîne trois compagnons, et les trois se
// décidaient jusqu'ici dans trois tables séparées — l'encre dans `atoms/Buttons/inkOnFill`, le
// relief dans `<Button>`, la tinte et son encre dans `<Badge>`. Trois tables sur le même objet,
// c'est trois occasions de décrire le même jeton différemment.
//
// Les règles que le descripteur porte, et qu'aucun composant ne redécide :
//
//  - **l'encre de l'aplat de l'UNIVERS s'inverse** (`text-on-primary` : blanche sur le marine du
//    clair, marine sur le bouton blanc de la nuit) — c'est le défaut ;
//  - **l'aplat d'une COULEUR ne s'inverse pas**, donc son encre non plus : blanche sur les aplats
//    foncés (`white`), marine sur les clairs (`text-on-accent` — le jaune, et l'orange d'alerte,
//    assez clair pour que du blanc dessus tombe à 2,6) ;
//  - **le relief reste foncé dans les deux thèmes**, d'où son propre jeton `relief-*` plutôt que
//    l'encre `*-strong`, qui s'éclaircit sur la nuit ;
//  - **la tinte est le fond clair de la chip teintée** (« +8 % »), avec l'encre qui va dessus.
//
// `theme/contrast.test.ts` ITÈRE cette table : un aplat ajouté amène ses paires avec lui, mesurées
// dans les deux thèmes — il n'y a plus de liste à tenir à la main en face.
// ───────────────────────────────────────────────────────────────────────────

/** La version teintée d'un aplat : fond clair + encre foncée. */
export type FillTint = { on: SemanticColorKey; ink: SemanticColorKey }

export type FillDescriptor = {
    /**
     * L'encre du texte et des glyphes posés sur l'aplat plein. Absent : une couleur d'AFFICHAGE, qui
     * ne porte pas de texte (`role` : un glyphe, un anneau, un trait).
     */
    ink?: SemanticColorKey
    /**
     * L'aplat quand la couleur habille un bouton PLEIN (`<Button>` principal, `<IconButton>`).
     * Absent : la couleur elle-même. Présent pour `danger` : le bouton qui détruit a son propre
     * aplat (`danger-fill`), que l'élève fonce, alors que le rouge d'un message ne bouge pas.
     */
    solid?: SemanticColorKey
    /**
     * Le libellé quand la couleur habille le bouton SECONDAIRE (face blanche dans les deux
     * thèmes). Absent : la couleur elle-même, qui ne s'inverse pas. Présent pour les couleurs de
     * l'univers, qui passent au blanc sur la nuit et disparaîtraient sur la face blanche.
     */
    outline?: SemanticColorKey
    /**
     * La couleur quand elle s'ÉCRIT à même une surface — le libellé du tertiaire, l'option non
     * choisie, l'onglet ouvert. Absent : la couleur elle-même. Présent pour les deux aplats de
     * l'univers, dont l'élève ne peut pas écrire la valeur telle quelle (ni son corail d'univers
     * ni l'orange de son bouton ne tiennent l'AA sur la page), et pour les couleurs de statut et
     * d'axe (`text-success`, `text-danger`… : un message d'erreur, un bandeau, un axe du score),
     * que l'élève écrit dans leur encre.
     */
    text?: SemanticColorKey
    /** Le ton foncé du relief d'un bouton-poussoir. Absent : le bouton reprend son propre aplat. */
    relief?: SemanticColorKey
    /** Absent : la couleur n'a pas de tinte — elle EST déjà la surface claire (le jaune). */
    tint?: FillTint
}

const FILLS = {
    primary: {
        ink: "text-on-primary",
        outline: "text-on-secondary",
        relief: "relief-primary",
        tint: { on: "primary-soft", ink: "primary" },
    },
    "primary-strong": { ink: "text-on-primary", outline: "text-on-secondary", relief: "relief-primary" },
    /** L'aplat de marque : marine dans les deux thèmes, donc encre blanche dans les deux. */
    "brand-fill": { ink: "white", relief: "relief-brand" },
    /** La lavande est un aplat CLAIR : elle s'écrit en encre, pas en blanc. */
    "primary-soft": { ink: "text-primary", tint: { on: "primary-soft", ink: "text-primary" } },
    /**
     * L'univers courant. `role` est sa couleur d'AFFICHAGE — un glyphe, un anneau, un trait, la
     * piste d'un interrupteur — et ne porte pas de texte : chez l'élève le blanc n'y tient que
     * 3,3:1. Il garde son écriture, celle de l'onglet ouvert. Ce qui s'écrit SUR l'univers prend
     * l'un de ses deux aplats : `role-selected`, une option choisie, et `role-fill`, son bouton.
     * Même encre et même relief — ceux du bouton de l'univers, que chaque thème repointe ; ils ne
     * se séparent que par leur écriture (`text`).
     */
    role: { text: "role-strong" },
    "role-selected": { ink: "text-on-role", outline: "text-on-secondary", text: "role-strong", relief: "relief-role" },
    "role-fill": { ink: "text-on-role", outline: "text-on-secondary", text: "text-link", relief: "relief-role" },
    info: {
        ink: "text-on-primary",
        outline: "text-on-secondary",
        relief: "relief-primary",
        tint: { on: "infoLight", ink: "info" },
    },
    accent: { ink: "text-on-accent", tint: { on: "accent", ink: "text-on-accent" } },
    "accent-strong": { ink: "text-on-accent" },
    success: {
        ink: "white",
        text: "text-success",
        relief: "relief-success",
        tint: { on: "successLight", ink: "success-strong" },
    },
    warning: {
        ink: "text-on-accent",
        text: "text-warning",
        relief: "relief-warning",
        tint: { on: "warningLight", ink: "warning-strong" },
    },
    danger: {
        ink: "white",
        solid: "danger-fill",
        text: "text-danger",
        tint: { on: "dangerLight", ink: "danger-strong" },
    },
    /** Le bouton qui détruit : le rouge plein, foncé chez l'élève (voir `danger-fill`). */
    "danger-fill": { ink: "white", relief: "relief-danger" },
    /**
     * Les aplats foncés. Pas de tinte : la version claire d'une alerte est déjà celle de
     * `warning` / `danger`, et en déclarer une seconde, identique, ne ferait que doubler les
     * paires que `contrast.test.ts` mesure.
     */
    "warning-deep": { ink: "white" },
    "danger-deep": { ink: "white" },
    purple: { ink: "white", text: "text-purple", relief: "relief-focused" },
    phone: { ink: "white", text: "text-phone" },
    /**
     * Les boutons des deux autres univers (kit V16), nommés : l'aplat élève `#C55016` sur son
     * relief `#993B0D` (orange depuis le 15/10), l'aplat tuteur `#1F7A8C` sur `#155866`. Encre blanche, et ils ne
     * s'inversent pas — une couleur, pas un ton de fond. Un composant ne les demande pas : il lit
     * `role-fill`, que le thème de l'univers pointe sur le sien. L'aplat élève a une tinte : la
     * chip de gain (`<Badge variant="role-student" tinted>`).
     */
    "role-student-fill": {
        ink: "white",
        relief: "relief-student",
        tint: { on: "role-student-tint", ink: "role-student-tint-ink" },
    },
    "role-tutor": { ink: "white", relief: "relief-tutor" },
} satisfies Partial<Record<SemanticColorKey, FillDescriptor>>

/**
 * Les aplats décrits. Les couleurs de MATIÈRE n'y sont pas : elles viennent du seeder backend,
 * hors palette V13, et aucun composant n'en dérive une encre — celui qui en peint une la nomme.
 */
export const fills: Partial<Record<SemanticColorKey, FillDescriptor>> = FILLS

/** L'encre à poser sur un aplat plein. Par défaut celle de l'univers, qui suit l'inversion. */
export function inkOnFill(fill: SemanticColorKey): SemanticColorKey {
    return fills[fill]?.ink ?? "text-on-primary"
}

/** L'aplat d'un bouton plein de cette couleur. Par défaut la couleur elle-même. */
export function solidFill(fill: SemanticColorKey): SemanticColorKey {
    return fills[fill]?.solid ?? fill
}

/** Le libellé du bouton secondaire de cette couleur. Par défaut la couleur elle-même. */
export function outlineInkOnFill(fill: SemanticColorKey): SemanticColorKey {
    return fills[fill]?.outline ?? fill
}

/** La couleur écrite à même une surface (tertiaire, option non choisie). Par défaut elle-même. */
export function textInkOnFill(fill: SemanticColorKey): SemanticColorKey {
    return fills[fill]?.text ?? fill
}

/**
 * Le ton foncé du relief d'un bouton-poussoir, par couleur d'aplat. Le livre n'en chiffre qu'un
 * — le principal de l'espace parent (`#1A2B66` → `#0C1640`) — et tranche le cas de la résiliation
 * (`danger` → `#873331`). À défaut de jeton dédié, l'aplat sert de relief à lui-même.
 */
export function reliefOnFill(fill: SemanticColorKey): SemanticColorKey {
    return fills[fill]?.relief ?? fill
}

/** La version teintée d'un aplat. À défaut, l'aplat EST déjà clair : il se pose tel quel. */
export function tintOnFill(fill: SemanticColorKey): FillTint {
    return fills[fill]?.tint ?? { on: fill, ink: inkOnFill(fill) }
}

/**
 * Les tintes : le fond clair que déclare un aplat pour sa chip teintée (`fills` → `tint.on`), la lavande
 * de `primary` comprise. Le jaune n'en est pas une : sa « tinte » est l'aplat lui-même.
 */
const TINTS: ReadonlySet<SemanticColorKey> = new Set(
    (Object.entries(fills) as [SemanticColorKey, FillDescriptor][]).flatMap(([fill, { tint }]) =>
        tint != null && tint.on !== fill ? [tint.on] : []
    )
)

export function isTint(color: SemanticColorKey): boolean {
    return TINTS.has(color)
}

/** Ce fond fait-il un encart pour ce qu'il porte ? La lavande des encarts, ou une tinte. */
export function isInsetSurface(background: SemanticColorKey): boolean {
    return background === "surface-subtle" || isTint(background)
}

/**
 * Le fond d'une pastille (une chip, le rond d'un glyphe) selon ce qui la porte : dans un encart, une tinte
 * n'a pas de fond (`null`) et la pastille garde son encre ; un aplat plein reste (`hooks/useOnInset`).
 */
export function pillFillOn(fill: SemanticColorKey, onInset: boolean): SemanticColorKey | null {
    return onInset && isTint(fill) ? null : fill
}
